import { ErrorHandler, Injectable } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { environment } from '../../environments/environment';
import * as Sentry from '@sentry/angular';

@Injectable()
export class SentryErrorHandler implements ErrorHandler {
  constructor() {}

  extractError(error) {
    // Try to unwrap zone.js error.
    // https://github.com/angular/angular/blob/master/packages/core/src/util/errors.ts
    if (error && error.ngOriginalError) {
      error = error.ngOriginalError;
    }

    // We can handle messages and Error objects directly.
    if (typeof error === 'string' || error instanceof Error) {
      return error;
    }

    // If it's http module error, extract as much information from it as we can.
    if (error instanceof HttpErrorResponse) {
      // The `error` property of http exception can be either an `Error` object, which we can use directly...
      if (error.error instanceof Error) {
        return error.error;
      }

      // ... or an`ErrorEvent`, which can provide us with the message but no stack...
      if (error.error instanceof ErrorEvent) {
        return error.error.message;
      }

      // ...or the request body itself, which we can use as a message instead.
      if (typeof error.error === 'string') {
        return `Server returned code ${error.status} with body "${error.error}"`;
      }

      // If we don't have any detailed information, fallback to the request message itself.
      return error.message;
    }

    // Skip if there's no error, and let user decide what to do with it.
    return null;
  }

  handleError(error: any) {
    const extractedError = this.extractError(error) || 'Handled unknown error';

    // Capture handled exception and send it to Sentry.
    Sentry.captureException(extractedError);

    // A stale index.html after a deploy requests hashed chunks that no longer
    // exist (version skew). Recover once by reloading to fetch the fresh index
    // (served no-store). The sessionStorage guard stops a genuinely-missing
    // chunk from looping — the second hit falls through to the /error screen.
    if (this.isChunkLoadError(error) && this.reloadOnce()) {
      return;
    }

    // When in development mode, log the error to console for immediate feedback.
    if (!environment.production) {
      console.error(extractedError);
    }
  }

  private isChunkLoadError(error: any): boolean {
    const message = error?.message ?? String(error ?? '');
    return (
      error?.name === 'ChunkLoadError' ||
      /Loading chunk [\w-]+ failed|dynamically imported module/.test(message)
    );
  }

  private reloadOnce(): boolean {
    // Only self-heal in production: in dev/HMR a stale chunk is expected and a
    // reload would swallow the console error. Key the one-shot flag by version
    // so a later deploy in the same session still gets one recovery attempt.
    if (!environment.production) {
      return false;
    }
    const RELOAD_KEY = 'cc_chunk_reload_' + environment.version;
    try {
      if (window.sessionStorage.getItem(RELOAD_KEY)) {
        return false;
      }
      window.sessionStorage.setItem(RELOAD_KEY, '1');
    } catch {
      // Storage blocked: skip the reload rather than loop with no memory.
      return false;
    }
    window.location.reload();
    return true;
  }
}
